import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { isValidElement, useMemo, useState } from 'react';
import { CardContentInner, FormGroupContextProvider, Loading, useEvent, } from 'react-admin';
import { WizardProgress } from './WizardProgress';
import { WizardToolbar } from './WizardToolbar';
import { WizardFormContext } from './WizardFormContext';
import { WizardFormStepProvider } from './WizardFormStepProvider';
import { useAuthorizedChildren, } from '../useAuthorizedChildren';
export var WizardFormView = function (_a) {
    var authorizationError = _a.authorizationError, children = _a.children, _b = _a.enableAccessControl, enableAccessControl = _b === void 0 ? false : _b, _c = _a.loading, loading = _c === void 0 ? DefaultLoading : _c, _d = _a.toolbar, toolbar = _d === void 0 ? DefaultToolbar : _d, _e = _a.progress, progress = _e === void 0 ? DefaultProgress : _e;
    var _f = useAuthorizedChildren({
        children: children,
        action: 'write',
        getResourceKey: getResourceKey,
        filterChildren: filterChildren,
        enabled: enableAccessControl,
    }), authorizedChildren = _f.authorizedChildren, isPending = _f.isPending, error = _f.error;
    var _g = useState(0), currentStep = _g[0], setCurrentStep = _g[1];
    var steps = React.Children.toArray(authorizedChildren)
        .filter(function (step) { return isValidElement(step); })
        .map(function (step) {
        var _a;
        return React.cloneElement(step, {
            enableAccessControl: (_a = step.props.enableAccessControl) !== null && _a !== void 0 ? _a : enableAccessControl,
        });
    });
    var hasPreviousStep = currentStep > 0;
    var hasNextStep = currentStep < steps.length - 1;
    var goToNextStep = useEvent(function () {
        setCurrentStep(function (step) {
            if (step === steps.length - 1)
                return step;
            return step + 1;
        });
    });
    var goToPreviousStep = useEvent(function () {
        setCurrentStep(function (step) {
            if (step === 0)
                return step;
            return step - 1;
        });
    });
    var goToStep = useEvent(function (index) {
        if (index < 0 || index > steps.length - 1)
            return;
        setCurrentStep(index);
    });
    var context = useMemo(function () { return ({
        currentStep: currentStep,
        hasPreviousStep: hasPreviousStep,
        hasNextStep: hasNextStep,
        goToNextStep: goToNextStep,
        goToPreviousStep: goToPreviousStep,
        goToStep: goToStep,
        steps: steps,
    }); }, [
        currentStep,
        hasPreviousStep,
        hasNextStep,
        goToNextStep,
        goToPreviousStep,
        goToStep,
        steps,
    ]);
    if (error) {
        return authorizationError;
    }
    if (enableAccessControl && isPending) {
        return loading;
    }
    return (_jsxs(WizardFormContext.Provider, { value: context, children: [progress, _jsx(CardContentInner, { children: steps.map(function (step, index) { return (_jsx(FormGroupContextProvider, { name: "step-".concat(index), children: _jsx(WizardFormStepProvider, { step: index, children: step }, step.key) }, step.key)); }) }), toolbar] }));
};
var DefaultProgress = _jsx(WizardProgress, {});
var DefaultToolbar = _jsx(WizardToolbar, {});
var DefaultLoading = _jsx(Loading, {});
var getResourceKey = function (child, resource) {
    var _a;
    return "".concat(resource, ".step.").concat((_a = child.props.id) !== null && _a !== void 0 ? _a : child.props.label);
};
var filterChildren = function (child) {
    if (child.props.id == null &&
        (child.props.label == null || child.props.label === '')) {
        if (process.env.NODE_ENV === 'development') {
            console.warn("You enabled access control on the WizardForm but didn't provide an id nor a label to its steps. Access control cannot be enforced on some steps.");
        }
        return false;
    }
    return true;
};
